/**
 * Dashboard - Mission Control
 * ===========================
 * The main hub after login. Shows stats, sites, and XP!
 * Like the cockpit of your spaceship.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { 
  Server, 
  Database, 
  Brain, 
  Zap,
  Plus,
  ChevronRight,
  Activity,
  HardDrive,
  Cpu,
  MemoryStick,
  LogOut,
  Trophy,
  Flame,
  Terminal,
  Container,
  Settings,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "GUIDES", icon: <Trophy className="w-5 h-5" />, path: "/tutorials" },
    { id: "family", label: "FAMILY", icon: <Flame className="w-5 h-5" />, path: "/family" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      {/* User Level Card */}
      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress 
          value={(user?.xp || 0) % 100} 
          className="h-2 bg-muted"
        />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">
                {user?.username?.[0]?.toUpperCase() || "?"}
              </span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button
            onClick={logout}
            className="text-muted-foreground hover:text-red-400 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ icon, label, value, subValue, color = "primary" }) => {
  const colorClasses = {
    primary: "text-primary border-primary/20 bg-primary/5",
    green: "text-green-400 border-green-400/20 bg-green-400/5",
    blue: "text-blue-400 border-blue-400/20 bg-blue-400/5",
    purple: "text-purple-400 border-purple-400/20 bg-purple-400/5",
  };

  return (
    <div className={`bg-[#16191F] border ${colorClasses[color]} p-6`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`${colorClasses[color]} p-2`}>
          {icon}
        </div>
        <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-3xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {value}
      </div>
      {subValue && (
        <div className="text-xs font-mono text-muted-foreground mt-1">{subValue}</div>
      )}
    </div>
  );
};

// Site Card Component
const SiteCard = ({ site, onClick }) => {
  return (
    <div 
      onClick={onClick}
      className="bg-[#16191F] border border-white/5 p-5 hover:border-primary/50 transition-all cursor-pointer group card-hover"
      data-testid={`site-card-${site.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Container className="w-5 h-5 text-blue-400" />
          <span className="font-mono text-white font-medium">{site.name}</span>
        </div>
        <div className={`w-3 h-3 rounded-full ${site.status === 'running' ? 'bg-green-400 status-online' : 'bg-red-400'}`} />
      </div>
      <div className="text-xs font-mono text-muted-foreground mb-4">
        {site.domain}
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs font-mono">
        <div className="flex items-center gap-1 text-muted-foreground">
          <Cpu className="w-3 h-3" />
          {site.cpu_usage}%
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <MemoryStick className="w-3 h-3" />
          {site.memory_usage}MB
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <HardDrive className="w-3 h-3" />
          {site.disk_usage}MB
        </div>
      </div>
      <div className="mt-3 flex items-center text-primary text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity">
        View Details <ChevronRight className="w-3 h-3 ml-1" />
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, sitesRes] = await Promise.all([
        api.get("/stats"),
        api.get("/sites"),
      ]);
      setStats(statsRes.data);
      setSites(sitesRes.data);
      await refreshUser();
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#0B0D10]">
        <Sidebar activePage="dashboard" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary animate-pulse mb-2">
              LOADING...
            </div>
            <p className="text-muted-foreground font-mono text-sm">
              Spinning up the hamster wheels...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="dashboard" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4 flex items-center justify-between">
            <div>
              <h1 
                className="text-2xl font-bold text-white uppercase tracking-tight"
                style={{ fontFamily: 'Chivo, sans-serif' }}
                data-testid="dashboard-title"
              >
                MISSION CONTROL
              </h1>
              <p className="text-xs font-mono text-muted-foreground mt-1">
                Welcome back, {user?.username}. Ready to build?
              </p>
            </div>
            <Button
              onClick={() => navigate("/sites")}
              className="bg-primary text-black font-bold hover:bg-primary/90"
              data-testid="new-site-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              NEW DEPLOYMENT
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard
              icon={<Server className="w-5 h-5" />}
              label="Total Sites"
              value={stats?.total_sites || 0}
              subValue={`${stats?.running_sites || 0} running`}
              color="blue"
            />
            <StatsCard
              icon={<Brain className="w-5 h-5" />}
              label="ML Models"
              value={stats?.ml_models || 0}
              subValue="Trained models"
              color="purple"
            />
            <StatsCard
              icon={<Zap className="w-5 h-5" />}
              label="XP Points"
              value={stats?.xp || 0}
              subValue={`${stats?.xp_to_next_level || 0} to next level`}
              color="primary"
            />
            <StatsCard
              icon={<Trophy className="w-5 h-5" />}
              label="Level"
              value={stats?.level || 1}
              subValue={`${stats?.streak || 0} day streak`}
              color="green"
            />
          </div>

          {/* Sites Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 
                className="text-xl font-bold text-white uppercase"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                YOUR DEPLOYMENTS
              </h2>
              <button 
                onClick={() => navigate("/sites")}
                className="text-primary text-sm font-mono flex items-center hover:underline"
                data-testid="view-all-sites-btn"
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {sites.length === 0 ? (
              <div className="bg-[#16191F] border border-white/5 p-12 text-center">
                <Container className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">No Deployments Yet</h3>
                <p className="text-muted-foreground font-mono text-sm mb-4">
                  Create your first site to get started!
                </p>
                <Button
                  onClick={() => navigate("/sites")}
                  className="bg-primary text-black font-bold"
                  data-testid="create-first-site-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Site
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sites.slice(0, 6).map((site) => (
                  <SiteCard 
                    key={site.id} 
                    site={site} 
                    onClick={() => navigate(`/sites/${site.id}`)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div>
            <h2 
              className="text-xl font-bold text-white uppercase mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              QUICK ACTIONS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate("/sites")}
                className="bg-[#16191F] border border-white/5 p-6 text-left hover:border-blue-400/50 transition-all group"
                data-testid="quick-deploy-btn"
              >
                <Container className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="text-white font-bold mb-1">Deploy Container</h3>
                <p className="text-xs text-muted-foreground font-mono">
                  Launch a new PHP site with Docker
                </p>
              </button>

              <button
                onClick={() => navigate("/ml-studio")}
                className="bg-[#16191F] border border-white/5 p-6 text-left hover:border-purple-400/50 transition-all group"
                data-testid="quick-ml-btn"
              >
                <Brain className="w-8 h-8 text-purple-400 mb-3" />
                <h3 className="text-white font-bold mb-1">Train Model</h3>
                <p className="text-xs text-muted-foreground font-mono">
                  Head to the Robot Gym
                </p>
              </button>

              <button
                onClick={() => sites[0] && navigate(`/sites/${sites[0].id}/files`)}
                disabled={sites.length === 0}
                className="bg-[#16191F] border border-white/5 p-6 text-left hover:border-primary/50 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="quick-code-btn"
              >
                <Database className="w-8 h-8 text-primary mb-3" />
                <h3 className="text-white font-bold mb-1">Code Editor</h3>
                <p className="text-xs text-muted-foreground font-mono">
                  Open your Code Deck
                </p>
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
